import React from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { X, Compass, Lightbulb, ArrowRight } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getTourSectionsForRole } from './tourContent'

interface FeatureTourModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * 功能導覽：一次講清楚側欄每個分頁在做什麼。
 *
 * 新夥伴第一次登入會自動開啟一次（由 TimerPage 依 localStorage 判斷），
 * 之後可從入職清單的「功能導覽」按鈕隨時再打開。
 */
const FeatureTourModal: React.FC<FeatureTourModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const { user } = useAuth()

  if (!isOpen) return null

  const sections = getTourSectionsForRole(user?.role)

  const goTo = (path: string) => {
    onClose()
    navigate(path)
  }

  // ⚠️ 一定要 portal 到 body：TimerPage 的根容器有 animate-fade-in，
  // animation 會建立新的 stacking context，把 position:fixed 的 overlay 困在裡面，
  // 導致 z-50 仍被 z-40 的側欄蓋住（畫面左緣被切掉）。
  return createPortal(
    <div className="modal-overlay">
      <div className="modal-content max-w-3xl w-full max-h-[85vh] flex flex-col">
        {/* 標題 */}
        <div className="flex items-center justify-between p-6 border-b border-primary-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <Compass className="w-6 h-6 text-primary-600" />
            <div>
              <h2 className="text-xl font-semibold text-secondary-800">功能導覽</h2>
              <p className="text-sm text-secondary-500">每個分頁是做什麼的，一次看懂</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="關閉功能導覽"
            className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 內容 */}
        <div className="p-6 space-y-6 overflow-y-auto">
          {sections.map(section => (
            <div key={section.title}>
              <h3 className="text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-3">
                {section.title}
              </h3>
              <div className="space-y-2">
                {section.items.map(item => (
                  <div
                    key={item.path}
                    className="rounded-xl border border-secondary-100 hover:border-primary-200
                      hover:bg-primary-50/40 transition-colors p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-800">{item.label}</p>
                        <p className="text-sm text-secondary-600 mt-0.5">{item.description}</p>
                        {item.tip && (
                          <div className="flex items-start gap-1.5 mt-2 text-xs text-amber-700
                            bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                            <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <span>{item.tip}</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => goTo(item.path)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                          rounded-lg flex-shrink-0 transition-colors text-primary-700
                          bg-primary-50 hover:bg-primary-100 border border-primary-200"
                      >
                        前往
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* 底部 */}
        <div className="flex justify-end p-6 border-t border-primary-200 flex-shrink-0">
          <button type="button" onClick={onClose} className="btn-primary">
            我知道了
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default FeatureTourModal
